import { Avatar, Skeleton } from 'antd';
import { selectSections } from 'containers/HomePage/selectors';
import { ISection } from 'containers/HomePage/types';
import React, { FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ApplicationRootState } from 'types';
import { Buyer, ProductBrand } from 'types/schema';
import { getInitials } from 'utils/getInitialsBrandName';
import HiroBg from './background/hiro.png';
import LionBg from './background/lion.png';
import DrippBg from './background/dripp.png';
import MasterMartiniBg from './background/masterMartini.png';
import MorinagaBg from './background/morinaga.png';
import OkamotoBg from './background/okamoto.png';
import OzaxBg from './background/ozax.png';
import DrippIcon from './svg/dripp.svg';
import HiroIcon from './svg/hiro.svg';
import LionIcon from './svg/lion.svg';
import MasterMartiniIcon from './svg/masterMartini.svg';
import MorinagaIcon from './svg/morinaga.svg';
import OkamotoIcon from './svg/okamoto.svg';

import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import translation from 'translations';
import messages from './messages';
import { selectBuyer } from 'containers/MainLayout/selectors';
import { commonDimensions } from 'utils/constants';

const Container = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto 20px;
  padding: 0 40px;
  @media (max-width: 1023px) {
    padding: 0 16px;
  }
  @media (max-width: 767px) {
    padding: 0px;
    margin-bottom: 12px;
  }
`;

const WidgetWrapper = styled.div`
  padding: 24px 0;
  background-color: #fff;
  border-radius: 16px;
  height: fit-content;
  display: grid;
  @media (max-width: 767px) {
    border-radius: 0px;
  }
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 24px;
  padding: 0 16px;
  @media (max-width: 767px) {
    margin-bottom: 8px;
    padding: 0;
  }
`;

const SectionTitleText = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  line-height: 28px;
  @media (max-width: 767px) {
    padding: 0 16px;
    font-size: 16px;
    line-height: 24px;
  }
`;

const List = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: hidden;
  padding: 1px;
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: 367px !important;
  @media (max-width: 767px) {
    width: 264px !important;
  }
`;

const SwiperElement = styled(Swiper)`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER - 32}px;
  padding: 4px 20px 0px !important;
  .swiper-button-next,
  .swiper-button-prev {
    width: 40px !important;
    height: 40px !important;
    border-radius: 50% !important;
    background-color: #ffffff !important;
    border: 1px solid #ffffff;
    box-shadow: 2px 6px 32px 0 rgba(112, 112, 112, 0.15);
    top: 50% !important;
    transform: translateY(-50%);
    &:after {
      font-size: 14px !important;
      color: #596056 !important;
      font-weight: 700;
    }
    &.swiper-button-disabled {
      display: none !important;
    }
    &:hover {
      border-color: #43a047;
      &:after {
        color: #43a047 !important;
      }
    }
  }
  @media (max-width: 767px) {
    .swiper-button-next,
    .swiper-button-prev {
      display: none;
    }
  }
  .swiper-button-next {
    right: 8px;
    &:after {
      margin-left: 1px;
    }
  }
  .swiper-button-prev {
    left: 8px;
    &:after {
      margin-right: 1px;
    }
  }
  @media (max-width: 767px) {
    padding: 4px 16px 0px !important;
  }
`;

const BrandCard = styled.div`
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const BrandImageSection = styled.div`
  position: relative;
  width: 100%;
  padding-top: 50.5%;
  overflow: visible;
`;

const BrandBgImage = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BrandLogoCircle = styled(Avatar)`
  position: absolute;
  bottom: -14px;
  left: 12px;
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  padding: 5px;
  width: 70px;
  height: 70px;
  img {
    object-fit: contain;
  }
  @media (max-width: 767px) {
    width: 49px;
    height: 49px;
    bottom: -10px;
  }
`;

const BrandLogoInitials = styled.div`
  position: absolute;
  bottom: -20px;
  left: 12px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.15);
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #242527;
`;

const BrandCardContent = styled.div`
  padding: 26px 20px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (max-width: 767px) {
    padding: 12px 16px 16px;
  }
`;

const BrandCardName = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #242527;
  @media (max-width: 767px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const BrandCardCategories = styled.div`
  font-size: 14px;
  color: #585a60;
  line-height: 26px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const BrandTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.88);
  line-height: 18px;
  width: fit-content;
  margin: 8px 0 20px;
  ${({ bg }) => `background: ${bg}`}
  @media (max-width: 767px) {
    gap: 4px;
    padding: 2px 6px;
  }
`;

const TagIcon = styled(SVGIcon)`
  @media (max-width: 767px) {
    & > div > img {
      width: 13px !important;
    }
  }
`;

const BrandTagText = styled.span`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const ViewDetailButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  border: 1px solid #eaebea;
  border-radius: 6px;
  font-size: 14px;
  color: #242527;
  cursor: pointer;
  margin-top: auto;
  transition: border-color 0.2s;

  &:hover {
    border-color: #43a047;
    color: #43a047;
    svg {
      color: #43a047 !important;
    }
  }
`;

interface IStateProps {
  sections: ISection;
  buyer: Buyer;
}

type Props = IStateProps;

const ChevronRightIcon = ({}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{ color: '#242527' }}
    >
      <path
        d="M5.39835 12.3667L10.3898 7.37081C10.4838 7.27672 10.5376 7.14159 10.5376 6.99996C10.5376 6.85833 10.4838 6.7232 10.3898 6.62911L5.39835 1.63318L4.81299 2.37431L9.43413 6.99996L4.81299 11.6256L5.39835 12.3667Z"
        fill="currentColor"
      />
      <path
        d="M4.76674 11.6205C4.76555 11.6436 4.77225 11.6664 4.78576 11.6837L5.36493 12.4248C5.39313 12.4609 5.44183 12.4643 5.47356 12.4322L10.4123 7.43631C10.5218 7.32555 10.5845 7.16648 10.5845 6.99995C10.5845 6.8334 10.5218 6.67434 10.4123 6.56358L5.47356 1.56765C5.44183 1.53562 5.39313 1.53898 5.36493 1.57506L4.78576 2.31619L4.77074 2.34581C4.7675 2.35652 4.76614 2.3679 4.76674 2.37942C4.76793 2.40259 4.77703 2.42439 4.79227 2.43981L9.29952 6.99995L4.79227 11.5601C4.77703 11.5755 4.76794 11.5973 4.76674 11.6205ZM9.46671 7.06546C9.48312 7.04885 9.49274 7.02494 9.49274 6.99995C9.49274 6.97495 9.48312 6.95104 9.46671 6.93443L4.95195 2.36689L5.42851 1.75621L10.3102 6.69461C10.3867 6.77204 10.4303 6.88326 10.4303 6.99995L10.4283 7.04324C10.4189 7.14382 10.3772 7.2375 10.3102 7.30528L5.42851 12.2431L4.95195 11.6324L9.46671 7.06546Z"
        fill="currentColor"
      />
    </svg>
  );
};

const BestForRestaurants: FC<Props> = ({ sections, buyer }) => {
  const brandsData = useMemo<ProductBrand[]>(() => {
    return (sections?.TRUSTED_BRANDS?.data.slice(0, 7) as ProductBrand[]) || [];
  }, [sections?.TRUSTED_BRANDS?.data]);
  const staticDataProd = {
    1208: {
      bg: DrippBg,
      categories: translation(messages.drippCategories) as string | undefined,
      tag: {
        bg: '#E6F9E7',
        icon: DrippIcon,
        text: translation(messages?.[1208]),
      },
    },
    1153: {
      bg: MasterMartiniBg,
      categories: undefined as string | undefined,
      tag: {
        bg: '#FFECE8',
        icon: MasterMartiniIcon,
        text: translation(messages?.[1153]),
      },
    },
    1154: {
      bg: MorinagaBg,
      categories: undefined as string | undefined,
      tag: {
        bg: '#FFFAE9',
        icon: MorinagaIcon,
        text: translation(messages?.[1154]),
      },
    },
    1175: {
      bg: OkamotoBg,
      categories: undefined as string | undefined,
      tag: {
        bg: '#E7F9FF',
        icon: OkamotoIcon,
        text: translation(messages?.[1175]),
      },
    },
    1177: {
      bg: LionBg,
      categories: undefined as string | undefined,
      tag: {
        bg: '#E6F9E7',
        icon: LionIcon,
        text: translation(messages?.[1177]),
      },
    },
    1176: {
      bg: OzaxBg,
      categories: undefined as string | undefined,
      tag: {
        bg: '#E7F9FF',
        icon: OkamotoIcon,
        text: translation(messages?.[1176]),
      },
    },
    1178: {
      bg: HiroBg,
      categories: undefined as string | undefined,
      tag: {
        bg: '#FFF0E5',
        icon: HiroIcon,
        text: translation(messages?.[1178]),
      },
    },
  };

  const staticDataQa = {
    1098: {
      bg: DrippBg,
      categories: translation(messages.drippCategories) as string | undefined,
      tag: {
        bg: '#E6F9E7',
        icon: DrippIcon,
        text: translation(messages?.[1208]),
      },
    },
    1092: {
      bg: MasterMartiniBg,
      tag: {
        bg: '#FFECE8',
        icon: MasterMartiniIcon,
        text: translation(messages?.[1153]),
      },
    },
    1093: {
      bg: MorinagaBg,
      tag: {
        bg: '#FFFAE9',
        icon: MorinagaIcon,
        text: translation(messages?.[1154]),
      },
    },
    1094: {
      bg: OkamotoBg,
      tag: {
        bg: '#E7F9FF',
        icon: OkamotoIcon,
        text: translation(messages?.[1175]),
      },
    },
    1095: {
      bg: LionBg,
      tag: {
        bg: '#E6F9E7',
        icon: LionIcon,
        text: translation(messages?.[1177]),
      },
    },
    1096: {
      bg: OzaxBg,
      tag: {
        bg: '#E7F9FF',
        icon: OkamotoIcon,
        text: translation(messages?.[1176]),
      },
    },
    1097: {
      bg: HiroBg,
      tag: {
        bg: '#FFF0E5',
        icon: HiroIcon,
        text: translation(messages?.[1178]),
      },
    },
  };

  const staticData = ['qa', 'development'].includes(process.env.APP_ENV) ? staticDataQa : staticDataProd;
  const regionLocal = window.localStorage.getItem('region') || 'HCM';
  if (buyer?.region === 'HN' || regionLocal === 'HN') {
    return null;
  }
  if (!brandsData.length) {
    return (
      <Container>
        <WidgetWrapper>
          <SectionTitleWrapper>
            <SectionTitleText>{translation(messages.sectionTitle)}</SectionTitleText>
          </SectionTitleWrapper>
          <Viewport.Consumer>
            {({ width }) => {
              const isDesktop = width >= 1024;
              return (
                <List>
                  <SwiperElement
                    slidesPerView="auto"
                    spaceBetween={16}
                    navigation={true}
                    modules={[Navigation, Mousewheel]}
                    mousewheel={{ forceToAxis: true }}
                    simulateTouch={true}
                    slidesPerGroup={isDesktop ? 2 : 1}
                  >
                    {Array.from({ length: 6 }).map((_, index) => (
                      <SwiperSlideElement key={index}>
                        <BrandCard>
                          <BrandImageSection>
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#f0f0f0',
                              }}
                            />
                            <Skeleton.Avatar
                              active
                              size={70}
                              style={{ position: 'absolute', bottom: '-14px', left: '12px' }}
                            />
                          </BrandImageSection>
                          <BrandCardContent>
                            <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: '80%' }} />
                            <Skeleton.Button active style={{ width: '100%', height: '40px', marginTop: '20px' }} />
                          </BrandCardContent>
                        </BrandCard>
                      </SwiperSlideElement>
                    ))}
                  </SwiperElement>
                </List>
              );
            }}
          </Viewport.Consumer>
        </WidgetWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <WidgetWrapper>
        <SectionTitleWrapper>
          <SectionTitleText>{translation(messages.sectionTitle)}</SectionTitleText>
        </SectionTitleWrapper>
        <Viewport.Consumer>
          {({ width }) => {
            const isDesktop = width >= 1024;
            return (
              <List>
                <SwiperElement
                  slidesPerView="auto"
                  spaceBetween={16}
                  navigation={true}
                  modules={[Navigation, Mousewheel]}
                  mousewheel={{ forceToAxis: true }}
                  simulateTouch={true}
                  slidesPerGroup={isDesktop ? 2 : 1}
                >
                  {brandsData.map((brand) => {
                    const categories =
                      staticData[Number(brand.id)]?.categories ||
                      brand.productCategories
                        ?.map((c) => c?.name)
                        .filter(Boolean)
                        .join(', ');

                    if (staticData[Number(brand.id)]?.bg === undefined) {
                      return null;
                    }

                    return (
                      <SwiperSlideElement key={brand.id}>
                        <BrandCard>
                          <BrandImageSection>
                            <BrandBgImage src={staticData[Number(brand.id)]?.bg} alt={brand.name || ''} />
                            {brand.imageUrl ? (
                              <BrandLogoCircle src={brand.imageUrl} />
                            ) : (
                              <BrandLogoInitials>{getInitials(brand.name || '')}</BrandLogoInitials>
                            )}
                          </BrandImageSection>

                          <BrandCardContent>
                            <BrandCardName>{brand.name}</BrandCardName>
                            <BrandCardCategories>{categories || 'Kem tươi, bơ'}</BrandCardCategories>
                            <BrandTag bg={staticData[Number(brand.id)]?.tag?.bg}>
                              <TagIcon src={staticData[Number(brand.id)]?.tag?.icon} />
                              <BrandTagText>{staticData[Number(brand.id)]?.tag?.text}</BrandTagText>
                            </BrandTag>
                            <Link to={`/thuong-hieu/${brand.slug}-b${brand.id}`}>
                              <ViewDetailButton onClick>
                                {translation(messages.viewDetail)}
                                <ChevronRightIcon />
                              </ViewDetailButton>
                            </Link>
                          </BrandCardContent>
                        </BrandCard>
                      </SwiperSlideElement>
                    );
                  })}
                </SwiperElement>
              </List>
            );
          }}
        </Viewport.Consumer>
      </WidgetWrapper>
    </Container>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  sections: selectSections(),
  buyer: selectBuyer(),
});

const withConnect = connect<IStateProps>(mapStateToProps);

export default withConnect(BestForRestaurants);
